import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import {
  CourseNodeRole,
  CourseNodeType,
  ICourse,
  ICourseNode,
  ICourseNodeDAO,
  ICourseOut,
  IModuleCourseNode,
} from 'types/domain/course';
import { reduceArrayById } from 'utils/reduceArrayById';
import { getEntities } from '../utils/getEntities';
import { parseVerification } from './verifications';
import { parseModule } from '../product/module';
import { parseCourseNodesAndEdges } from './flow';
import { Node } from '@xyflow/react';
import { createCachedSelector } from 're-reselect';
import { sortByDateModified } from './courseProgress';
import { parseOrganisationSystemDAO } from './organisationSystems';
import { parseCertificateDAO } from './certificate';
import { isReviewNode } from './courseNode';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id: number) => (id ? id : state.state[EntityTypes.COURSE]);

export const selectModuleCourseNodes = createCachedSelector(entity, id, (entity: IEntityReducer, id: number) => {
  const flowData = id
    ? parseCourseNodesAndEdges(entity, id, true)
        ?.nodes.map((node: Node) => entity[EntityTypes.COURSE_NODE][node.data.id as number])
        .filter((courseNode: ICourseNode) => !!courseNode.moduleId)
    : getEntities(entity, EntityTypes.COURSE_NODE)
        .filter((courseNode: ICourseNode) => !!courseNode.moduleId)
        .sort(sortByDateModified)
        .reverse();

  const seen = new Set<number>();
  return flowData
    .filter((courseNode: ICourseNode) => {
      if (seen.has(courseNode.moduleId)) {
        return false;
      }
      seen.add(courseNode.moduleId);
      return true;
    })
    .map((courseNode: ICourseNode) => {
      return {
        ...courseNode,
        node: courseNode.nodeId ? entity[EntityTypes.NODE][courseNode.nodeId] : null,
        text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
        image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
        module: parseModule(entity, courseNode.moduleId),
      } as IModuleCourseNode;
    });
})((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');

/**
 * Module course nodes that are actually *connected* in the course flow, walked
 * in order from the course's start node (`course.courseNodeId`) via `childId`
 * and course outs.
 *
 * Deliberately does not go through `parseCourseNodesAndEdges`: that function
 * appends every unconnected course node once `selectAll` is set — or whenever
 * the node has a saved canvas position — which makes it useless for answering
 * "is this module in the course?".
 */
export function parseCourseFlowModuleNodes(entity: IEntityReducer, id: number): IModuleCourseNode[] {
  const course: ICourse = entity[EntityTypes.COURSE][id];

  if (!course?.courseNodeId) return [];

  const courseNodesById = reduceArrayById(getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id));

  const outsBySource: Record<number, ICourseOut[]> = {};
  getEntities(entity, EntityTypes.COURSE_OUT, 'courseId', id).forEach((out: ICourseOut) => {
    if (!outsBySource[out.sourceId]) outsBySource[out.sourceId] = [];
    outsBySource[out.sourceId].push(out);
  });

  const ordered: ICourseNode[] = [];
  const visited = new Set<number>();

  function walk(nodeId: number) {
    const courseNode: ICourseNode = courseNodesById[nodeId];

    if (!courseNode || visited.has(courseNode.id) || courseNode.status < 0) return;

    visited.add(courseNode.id);
    ordered.push(courseNode);

    [
      ...(courseNode.childId ? [courseNode.childId] : []),
      ...(outsBySource[courseNode.id] || []).map(out => out.targetId),
    ].forEach(walk);
  }

  walk(course.courseNodeId);

  const seen = new Set<number>();
  return ordered
    .filter((courseNode: ICourseNode) => {
      if (!courseNode.moduleId || seen.has(courseNode.moduleId)) return false;
      seen.add(courseNode.moduleId);
      return true;
    })
    .map((courseNode: ICourseNode) => {
      return {
        ...courseNode,
        node: courseNode.nodeId ? entity[EntityTypes.NODE][courseNode.nodeId] : null,
        text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
        image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
        module: parseModule(entity, courseNode.moduleId),
      } as IModuleCourseNode;
    });
}

export const selectCourseFlowModuleNodes = createCachedSelector(
  entity,
  id,
  parseCourseFlowModuleNodes,
)((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');

export function parseCourseNodeDAO(entity, id) {
  const courseNode: ICourseNode = entity[EntityTypes.COURSE_NODE][id];

  if (!courseNode) return null;

  const outs = getEntities(entity, EntityTypes.COURSE_OUT, 'sourceId', courseNode.id);

  return {
    ...courseNode,
    text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
    image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
    entities: getEntities(entity, EntityTypes.COURSE_NODE_ENTITY, 'courseNodeId', courseNode.id).map(entity => {
      return { ...entity, outs: outs.filter((out: ICourseOut) => out.sourceHandle === entity.id) };
    }),
    courseOuts: outs,
    variable: courseNode.variableId ? entity[EntityTypes.VARIABLE][courseNode.variableId] : null,
    verification: courseNode.verificationId
      ? parseVerification(entity, courseNode.verificationId, courseNode.id)
      : null,
  } as ICourseNodeDAO;
}

export const selectStartCourseNodes = createCachedSelector(entity, id, (entity: IEntityReducer, id: number) => {
  return filterCourseNodes(
    entity,
    id,
    (courseNode: ICourseNode) =>
      courseNode.status > -1 && courseNode.type === CourseNodeType.START && !isReviewNode(courseNode),
    //&& [CourseNodeRole.SCORM, CourseNodeRole.SHARE, CourseNodeRole.API].includes(courseNode.entityType),
  );
})((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');

export const selectSystemCourseNodes = createCachedSelector(entity, id, (entity: IEntityReducer, id: number) => {
  return filterCourseNodes(
    entity,
    id,
    (courseNode: ICourseNode) => courseNode.status > -1 && courseNode.entityType === CourseNodeRole.SYSTEM,
  );
})((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');

export const selectCertificateCourseNodes = createCachedSelector(entity, id, (entity: IEntityReducer, id: number) => {
  return filterCourseNodes(
    entity,
    id,
    (courseNode: ICourseNode) =>
      courseNode.status > -1 &&
      courseNode.type === CourseNodeType.END &&
      courseNode.entityType === CourseNodeRole.CERTIFICATE,
  );
})((state: RootState, id?: number) => id || state.state[EntityTypes.COURSE] || 'unknown');

function filterCourseNodes(entity: IEntityReducer, id: number, filter: (courseNode: ICourseNode) => boolean) {
  return getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id)
    .filter(filter)
    .map((courseNode: ICourseNode) => {
      return {
        ...courseNode,
        node: courseNode.nodeId ? entity[EntityTypes.NODE][courseNode.nodeId] : null,
        text: courseNode.textId ? entity[EntityTypes.TEXT][courseNode.textId] : null,
        image: courseNode.imageId ? entity[EntityTypes.IMAGE][courseNode.imageId] : null,
        organisationSystem: parseOrganisationSystemDAO(entity, courseNode.organisationSystemId),
        // Certificate END nodes carry only certificateId; without this the
        // certificate page renders its preview from `undefined` and falls back
        // to the generic title, so the course's own certificate never shows.
        certificate: parseCertificateDAO(entity, courseNode.certificateId),
        // Genuinely partial — this shape has never carried module/verification/
        // variable, and the direct cast only compiled while `certificate` was
        // also absent.
      } as unknown as ICourseNodeDAO;
    });
}
